import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectsWithStats } from "@/hooks/useProjects";

export function ProjectHealthCards() {
  const { data: projects, isLoading } = useProjectsWithStats();
  const navigate = useNavigate();

  // Roll up child project items into parent, only show top-level with activity
  const topLevelProjects = useMemo(() => {
    if (!projects) return [];

    const projectMap = new Map(projects.map((p) => [p.id, p]));

    // Build rolled-up counts: parent gets its own items + all children's items
    const rolledUp = new Map<string, { project: typeof projects[0]; totalItems: number }>();

    for (const p of projects) {
      if (p.parent_id && projectMap.has(p.parent_id)) {
        // Child project — add items to parent
        const parentId = p.parent_id;
        if (!rolledUp.has(parentId)) {
          const parent = projectMap.get(parentId)!;
          rolledUp.set(parentId, { project: parent, totalItems: parent.activeItemCount });
        }
        rolledUp.get(parentId)!.totalItems += p.activeItemCount;
      } else {
        // Top-level project
        if (!rolledUp.has(p.id)) {
          rolledUp.set(p.id, { project: p, totalItems: p.activeItemCount });
        }
      }
    }

    // Only show projects with active items
    return Array.from(rolledUp.values())
      .filter((r) => r.totalItems > 0)
      .sort((a, b) => b.totalItems - a.totalItems);
  }, [projects]);

  if (isLoading) return null;

  if (topLevelProjects.length === 0) {
    return (
      <p className="text-muted-foreground text-sm text-center py-4">
        No projects with active items
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {topLevelProjects.map(({ project, totalItems }) => (
        <div
          key={project.id}
          className="rounded-lg border border-border p-3 bg-card cursor-pointer hover:bg-accent/10 transition-colors"
          onClick={() => navigate(`/projects/${project.slug}`)}
        >
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: project.color }}
            />
            <span className="text-sm font-medium truncate">
              {project.name}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {totalItems} active item{totalItems !== 1 ? "s" : ""}
          </p>
        </div>
      ))}
    </div>
  );
}
